/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { t } from '@sportninja/common/i18n'
import PropTypes from 'prop-types'
import React from 'react'
import GameLocationCard from '../../components/GameLocationCard'
import GameOfficials from '../../components/GameOfficials'
import ScoresheetCard from '../../components/ScoresheetCard'
import SignInSheetCard from '../../components/SignInSheetCard'
import GameSummaryCard from '../../components/GameSummaryCard'
import GameBoxScore from './GameBoxScore'
import GameDetailsStats from './GameDetailsStats'
import ShootoutCard from './ShootoutCard'
import { Container } from 'src/components/Layout'

const Details = ({
  game,
  isSoccer = false,
  isGenericGame = false,
  isMobile = false,
  statsUrl,
  isPublicRoute = false,
}) => {
  if (!game) {
    return false
  }

  const { referees, scorekeepers, location, facility } = game
  const officials = game?.officials || [...referees, ...scorekeepers]
  const hasMoreThanOnePeriod = game?.periods?.length > 1

  // Feature flag check for AI summary - only explicitly true means enabled
  const featureFlags = game?.feature_flags
  const hasValidFeatureFlags =
    featureFlags &&
    typeof featureFlags === 'object' &&
    !Array.isArray(featureFlags)
  const isAiSummaryEnabled =
    hasValidFeatureFlags && featureFlags['ai-summary'] === true

  // Determine if we should display period-related components
  let shouldDisplayPeriodStuffs = true
  if (isGenericGame) {
    if (!hasMoreThanOnePeriod) {
      shouldDisplayPeriodStuffs = false
    } else {
      shouldDisplayPeriodStuffs = true
    }
  } else {
    shouldDisplayPeriodStuffs = true
  }

  // Prepare game summary data
  const gameSummary = game?.game_summary
    ? {
        title: game.game_summary.headline,
        content: game.game_summary.summary,
      }
    : null

  return (
    <Container
      noWrap
      row
      css={css`
        display: flex;
        width: 100%;
        align-items: flex-start;
        margin-bottom: 16px;
        @media (max-width: 768px) {
          margin-left: 0;
          flex-direction: column;
          align-items: center;
          padding-bottom: 16px;
        }
      `}
      wrapCss={css`
        gap: 16px;
      `}
    >
      {/* First Column: Box Score, Scoresheet, Sign-in Sheet, Location */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
          flex: 0 0 calc((100% - 32px) / 3);
          width: calc((100% - 32px) / 3);
          @media (max-width: 768px) {
            flex: 0 0 100%;
            width: 100%;
          }
        `}
      >
        {shouldDisplayPeriodStuffs && (
          <GameBoxScore
            game={game}
            isMobile={isMobile}
            isSoccer={isSoccer}
            isGenericGame={isGenericGame}
          />
        )}
        {!isPublicRoute ? (
          <ShootoutCard game={game} isMobile={isMobile} />
        ) : null}

        <ScoresheetCard gameId={game?.id} gameNumber={game?.gameNumber} />
        <SignInSheetCard gameId={game?.id} gameNumber={game?.gameNumber} />
        <GameLocationCard
          venue={location}
          title={t('common:location')}
          facility={facility}
          isMobile={isMobile}
        />
      </div>

      {/* Second Column: Team Stats, Game Officials */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
          flex: 0 0 calc((100% - 32px) / 3);
          width: calc((100% - 32px) / 3);
          @media (max-width: 768px) {
            flex: 0 0 100%;
            width: 100%;
            margin-top: 16px;
          }
        `}
      >
        <GameDetailsStats
          game={game}
          isMobile={isMobile}
          isSoccer={isSoccer}
          isGenericGame={isGenericGame}
          statsUrl={statsUrl}
        />
        <GameOfficials officials={officials} />
      </div>

      {/* Third Column: Game Summary (if exists and enabled) */}
      {gameSummary && isAiSummaryEnabled && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            flex: 0 0 calc((100% - 32px) / 3);
            width: calc((100% - 32px) / 3);
            @media (max-width: 768px) {
              flex: 0 0 100%;
              width: 100%;
              margin-top: 16px;
            }
          `}
        >
          <GameSummaryCard summary={gameSummary} isMobile={isMobile} />
        </div>
      )}
    </Container>
  )
}

Details.propTypes = {
  game: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
}

export default Details
